import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { UIMemoIndex } from '../model/UiShapes';
import { handleError } from '../hooks/managers/errorHandler';

// 映射表类型定义
export interface LabelToMemoGuidMap {
  [label: string]: string[]; // label -> memo guid列表
}

export interface MemoIndexState {
  // State
  memoIndexes: UIMemoIndex[];
  selectedTag: string;
  currentMemoIndex: UIMemoIndex | null;
  status: 'idle' | 'loading' | 'failed';
  error: string | null;
  lastUpdated: number;

  // 映射表状态
  labelToMemoGuidMap: LabelToMemoGuidMap;

  // Actions
  setMemoIndexes: (memoIndexes: UIMemoIndex[]) => void;
  addMemoIndex: (memoIndex: UIMemoIndex) => void;
  updateMemoIndex: (memoId: string, memoIndex: UIMemoIndex) => void;
  setSelectedTag: (memoId: string) => void;
  setCurrentMemoIndex: (memoIndex: UIMemoIndex | null) => void;
  setStatus: (status: 'idle' | 'loading' | 'failed') => void;
  setError: (error: string | null) => void;
  setLastUpdated: (timestamp: number) => void;
  clearError: () => void;
  clear: () => void;

  // 映射表相关Actions
  buildLabelToMemoGuidMap: () => void;
  getMemosByLabel: (label: string) => string[];
  searchMemosByLabel: (searchKey: string) => string[];

  // 强制刷新方法
  forceRefreshMemoIndexByMemoId: (memoIndex: UIMemoIndex | null) => void;
}

// 创建memo index store
export const useMemoIndexStore = create<MemoIndexState>()(
  devtools(
    (set, get) => ({
      // 初始状态
      memoIndexes: [],
      selectedTag: '',
      currentMemoIndex: null,
      status: 'idle',
      error: null,
      lastUpdated: 0,

      // 映射表初始状态
      labelToMemoGuidMap: {},

      // 同步actions
      setMemoIndexes: (memoIndexes: UIMemoIndex[]) => {
        set({
          memoIndexes,
          lastUpdated: Date.now(),
          error: null
        });
      },

      addMemoIndex: (memoIndex: UIMemoIndex) => {
        const currentIndexes = get().memoIndexes;
        const existingIndex = currentIndexes.findIndex((index) => index.guid === memoIndex.guid);

        if (existingIndex >= 0) {
          // 更新现有的memo index
          const updatedIndexes = [...currentIndexes];
          updatedIndexes[existingIndex] = memoIndex;
          set({
            memoIndexes: updatedIndexes,
            lastUpdated: Date.now()
          });
        } else {
          // 添加新的memo index
          set({
            memoIndexes: [...currentIndexes, memoIndex],
            lastUpdated: Date.now()
          });
        }
      },

      updateMemoIndex: (memoId: string, memoIndex: UIMemoIndex) => {
        const currentIndexes = get().memoIndexes;
        const existingIndex = currentIndexes.findIndex((index) => index.guid === memoId);

        if (existingIndex >= 0) {
          const updatedIndexes = [...currentIndexes];
          updatedIndexes[existingIndex] = memoIndex;
          set({
            memoIndexes: updatedIndexes,
            lastUpdated: Date.now()
          });
        }
      },

      setSelectedTag: (memoId: string) => {
        set({ selectedTag: memoId });

        // 同时设置currentMemoIndex
        const currentIndexes = get().memoIndexes;
        const foundIndex = currentIndexes.find((index) => index.guid === memoId);
        set({ currentMemoIndex: foundIndex || null });
      },

      setCurrentMemoIndex: (memoIndex: UIMemoIndex | null) => {
        set({ currentMemoIndex: memoIndex });
      },

      setStatus: (status: 'idle' | 'loading' | 'failed') => {
        set({ status });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      setLastUpdated: (timestamp: number) => {
        set({ lastUpdated: timestamp });
      },

      clearError: () => {
        set({ error: null });
      },

      clear: () => {
        set({
          memoIndexes: [],
          selectedTag: '',
          currentMemoIndex: null,
          status: 'idle',
          error: null,
          lastUpdated: 0,
          labelToMemoGuidMap: {}
        });
      },

      // 映射表相关Actions
      buildLabelToMemoGuidMap: () => {
        const state = get();
        const newMap: LabelToMemoGuidMap = {};

        let totalLabels = 0;
        let totalMappings = 0;

        // 遍历所有memo indexes，构建映射表
        state.memoIndexes.forEach((memoIndex: UIMemoIndex, index) => {
          if (memoIndex.indexes && Array.isArray(memoIndex.indexes)) {
            memoIndex.indexes.forEach((indexItem, itemIndex) => {
              const label = indexItem.label?.toLowerCase().trim();
              if (label) {
                if (!newMap[label]) {
                  newMap[label] = [];
                  totalLabels++;
                }
                // 添加memo guid到对应的label列表中
                if (!newMap[label].includes(memoIndex.guid)) {
                  newMap[label].push(memoIndex.guid);
                  totalMappings++;
                }
              }
            });
          }
        });

        set({ labelToMemoGuidMap: newMap });
      },

      getMemosByLabel: (label: string) => {
        const state = get();
        const normalizedLabel = label.toLowerCase().trim();
        return state.labelToMemoGuidMap[normalizedLabel] || [];
      },

      searchMemosByLabel: (searchKey: string) => {
        const state = get();
        const normalizedSearchKey = searchKey.toLowerCase().trim();
        const result: string[] = [];

        // 遍历所有label，查找包含搜索关键词的label
        Object.keys(state.labelToMemoGuidMap).forEach((label) => {
          if (label.includes(normalizedSearchKey)) {
            result.push(...state.labelToMemoGuidMap[label]);
          }
        });

        // 去重（兼容性写法）
        const uniqueResult: string[] = [];
        const seen = new Set<string>();
        result.forEach((guid) => {
          if (!seen.has(guid)) {
            seen.add(guid);
            uniqueResult.push(guid);
          }
        });
        return uniqueResult;
      },

      // 强制刷新特定memo的index数据（同步版本，由Manager层调用）
  forceRefreshMemoIndexByMemoId: (memoIndex: UIMemoIndex | null) => {
    if (memoIndex) {
      // 更新store状态
      get().addMemoIndex(memoIndex);
      set({ status: 'idle', error: null });
    } else {
      // 清除对应的缓存 - 当memoIndex为null时，不需要清除特定memo的缓存
      set({ status: 'idle', error: null });
    }
  }
    }),
    {
      name: 'memo-index-store',
      serialize: {
        options: true
      }
    }
  )
);

// Selectors
export const selectMemoIndexes = () => useMemoIndexStore.getState().memoIndexes;
export const selectMemoIndexByGuid = (memoId: string) => useMemoIndexStore.getState().memoIndexes.find((index) => index.guid === memoId);
export const selectCurrentMemoIndex = () => useMemoIndexStore.getState().currentMemoIndex;
export const selectSelectedTag = () => useMemoIndexStore.getState().selectedTag;
export const selectMemoIndexStatus = () => useMemoIndexStore.getState().status;
export const selectMemoIndexError = () => useMemoIndexStore.getState().error;
export const selectMemoIndexLastUpdated = () => useMemoIndexStore.getState().lastUpdated;

// React hooks for using selectors
export const useMemoIndexes = () => useMemoIndexStore((state) => state.memoIndexes);
export const useCurrentMemoIndex = () => useMemoIndexStore((state) => state.currentMemoIndex);
export const useSelectedTag = () => useMemoIndexStore((state) => state.selectedTag);
export const useMemoIndexStatus = () => useMemoIndexStore((state) => state.status);
export const useMemoIndexError = () => useMemoIndexStore((state) => state.error);
